import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { router } from './app/router';
import { initNative } from './lib/native';
import { initAnalytics, trackPage } from './lib/analytics';
import { AuthProvider } from './lib/auth';
import { applyTheme, useTheme } from './lib/theme';
import { onLanguageChange, setLanguage } from './i18n';
import { setAuthLanguage } from './lib/firebase';
import { resolveLocale, usePrefs } from './lib/prefs';
import './styles/fonts.css';
import './styles/tokens.css';
import './styles/app.css';

applyTheme(useTheme.getState().theme);
initNative(
  (path) => void router.navigate({ to: path }),
  () => router.history.back(),
  () => window.history.length > 1 && router.state.location.pathname !== '/trees' && router.state.location.pathname !== '/',
);

// Store-screenshot builds never send analytics.
if (import.meta.env.VITE_SHOTS !== '1') {
  void initAnalytics();
  router.subscribe('onResolved', (e) => {
    if (e.pathChanged) trackPage(e.toLocation.pathname);
  });
}

const client = new QueryClient({ defaultOptions: { queries: { staleTime: 10_000, retry: 1, refetchOnWindowFocus: false } } });

const render = () =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={client}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );

// Store-screenshot builds answer the API from the sample family (scripts/screenshots.mjs); normal builds drop this.
// The screen language loads before the first paint, so nobody sees English flash first.
onLanguageChange(setAuthLanguage);
const ready = setLanguage(resolveLocale(usePrefs.getState().locale)).catch(() => undefined);
if (import.meta.env.VITE_SHOTS === '1') void ready.then(() => import('./shots/mock')).then((m) => (m.installMockApi(), render()));
else void ready.then(render);
